import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { wsUrl } from "../../constants"; // e.g. http://localhost:3000
import { getUserInfoFromToken } from "../../utils/Main";

type ChatCtx = {
  socket: Socket | null;
  connected: boolean;
};

const ChatSocketContext = createContext<ChatCtx>({ socket: null, connected: false });
export const useChatSocket = () => useContext(ChatSocketContext);

/**
 * Single socket.io connection for the whole app.
 * - Namespace: /chat  (change to wsUrl if your gateway is root)
 * - Sends { token, userId } in handshake auth (most gateways need one or both).
 * - Reconnects when token changes (same-tab + cross-tab).
 */
export const ChatSocketProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  const getAuth = () => {
    const token =
      localStorage.getItem("accessToken") || localStorage.getItem("token") || "";
    const info = getUserInfoFromToken?.(); // { id, ... } if you have it
    const userId = info?.id;
    return { token, userId };
  };

  // Track token in state so we can reconnect with new auth when it changes
  const [auth, setAuth] = useState(getAuth);

  // Create the socket once
  useEffect(() => {
    if (socketRef.current) return;

    // ⚠️ if your gateway is NOT namespaced, use io(wsUrl, {...})
    const s = io(`${wsUrl}/chat`, {
      path: "/ws",
      transports: ["websocket"],
      withCredentials: true,
      autoConnect: false,
      auth, // initial auth sent in handshake
    });

    socketRef.current = s;

    const onConnect = () => {
      setConnected(true);
      console.log("[socket] connected", s.id);
      // ask server for unread map every connect
      s.emit("unread.request");
    };
    const onDisconnect = () => setConnected(false);
    const onError = (e: any) => console.warn("[socket] connect_error", e?.message || e);

    // TEMP: log every incoming event until you’re happy, then remove
    const onAny = (event: string, ...args: any[]) => {
      console.log("[SOCKET onAny]", event, args?.[0] ?? "");
    };

    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("connect_error", onError);
    s.onAny(onAny);

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("connect_error", onError);
      s.offAny(onAny);
      s.close();
      socketRef.current = null;
    };
  }, []);

  // React to token changes: update handshake auth, connect/disconnect appropriately
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;

    (s as any).auth = auth;
    if (auth.token) {
      if (!s.connected) s.connect();
    } else {
      if (s.connected) s.disconnect();
    }
  }, [auth]);

  // Listen for token changes (cross-tab via 'storage', same-tab via custom event)
  useEffect(() => {
    const refreshAuth = () => setAuth(getAuth());
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accessToken" || e.key === "token") refreshAuth();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("auth-token-changed", refreshAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth-token-changed", refreshAuth);
    };
  }, []);

  return (
    <ChatSocketContext.Provider value={{ socket: socketRef.current, connected }}>
      {children}
    </ChatSocketContext.Provider>
  );
};
